import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api, pdfUrls } from '@/api';
import { Spinner } from '@/components/Spinner';

const PAGE_SIZE = 10;
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
function formatDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

export default function AdminReportPage() {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filter, setFilter] = useState('');
  const [agentId, setAgentId] = useState<number | null>(null);
  const [over300k, setOver300k] = useState(false);
  const [page, setPage] = useState(0);

  const { data: agents = [] } = useQuery({
    queryKey: ['admin-agents'],
    queryFn: () => api.listAgents(),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['admin-report', { from, to, filter, over300k }],
    queryFn: () => api.listReport({ from, to, filter, over300k }),
  });

  const paidMutation = useMutation({
    mutationFn: ({ id, mid }: { id: number; mid: number }) => api.markPaid(id, mid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-report'] }),
  });

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(
    () => rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [rows, safePage],
  );

  const reset = () => { setFrom(''); setTo(''); setFilter(''); setAgentId(null); setOver300k(false); setPage(0); };

  return (
    <>
      <div className="ast-filters">
        <div className="ast-filter-field">
          <label className="ast-field-label" htmlFor="rep-from">From</label>
          <input id="rep-from" type="date" className="form-control form-control-sm"
            value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
        </div>
        <div className="ast-filter-field">
          <label className="ast-field-label" htmlFor="rep-to">To</label>
          <input id="rep-to" type="date" className="form-control form-control-sm"
            value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
        </div>
        <div className="ast-filter-field ast-filter-grow">
          <label className="ast-field-label" htmlFor="rep-agent">Agent</label>
          <select
            id="rep-agent"
            className="form-select form-select-sm"
            value={agentId ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value);
              setAgentId(val);
              setFilter(agents.find((a) => a.userId === val)?.name ?? '');
              setPage(0);
            }}
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.userId} value={a.userId}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="form-check ast-over300k">
          <input className="form-check-input" type="checkbox" id="rep-over300k"
            checked={over300k} onChange={(e) => { setOver300k(e.target.checked); setPage(0); }} />
          <label className="form-check-label" htmlFor="rep-over300k">&gt; $300,000</label>
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            if (!from || !to) { window.alert('Please choose a From and To date for the report.'); return; }
            const a = agents.find((x) => x.userId === agentId);
            if (!a) { window.alert('Please select an agent for the report.'); return; }
            window.open(pdfUrls.remit(from, to, a.userId, a.orgId), '_blank', 'noopener');
          }}
        >
          Report
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={reset}>Reset</button>
      </div>

      <div className="ast-card">
        <table className="table table-hover align-middle ast-table mb-0">
          <thead>
            <tr>
              <th className="text-center">Paid</th>
              <th>Agent Name</th>
              <th>File #</th>
              <th>Jacket #</th>
              <th>Date</th>
              <th>Type</th>
              <th className="text-end">Liability Amount</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-4"><Spinner /></td></tr>}
            {!isLoading && pageRows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-4">No billable items found.</td></tr>
            )}
            {!isLoading && pageRows.map((r) => (
              <tr key={`${r.id}-${r.mid}`} className={r.paid ? 'ast-row-void' : ''}>
                <td className="text-center">
                  {r.paid
                    ? <i className="bi bi-check-circle-fill text-success" title="Paid" />
                    : <i className="bi bi-circle text-muted" title="Unpaid" />}
                </td>
                <td>{r.agentName}</td>
                <td>{r.fileNumber}</td>
                <td>{r.jacketNumber}</td>
                <td>{formatDate(r.date)}</td>
                <td>{r.type}</td>
                <td className="text-end">{money.format(r.liabilityAmount)}</td>
                <td className="text-end">
                  <button className="btn btn-sm btn-outline-success" disabled={r.paid}
                    onClick={() => paidMutation.mutate({ id: r.id, mid: r.mid })}>
                    {r.paid ? 'Paid' : 'Mark Paid'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ast-pager">
        <button className="btn btn-sm btn-outline-secondary" disabled={safePage <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))} title="Previous page">
          <i className="bi bi-chevron-left" />
        </button>
        <span className="ast-pager-info">
          Showing {rows.length === 0 ? 0 : safePage * PAGE_SIZE + 1}–
          {Math.min(rows.length, (safePage + 1) * PAGE_SIZE)} of {rows.length}
        </span>
        <button className="btn btn-sm btn-outline-secondary" disabled={safePage >= pageCount - 1}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} title="Next page">
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </>
  );
}
